/**
 * Stores a value in the local storage using a composite key.
 *
 * @param pixel - A string used as part of the composite key.
 * @param key - A string used as part of the composite key.
 * @param value - The value to be stored in local storage. It will be serialized to a JSON string.
 */
export const cacheStoreInLocal = (pixel: string, key: string, value: unknown): void => {
  localStorage.setItem((pixel + '_' + key).toLocaleUpperCase(), JSON.stringify(value))
}

const isValidType = <T>(value: unknown, defaultValue: T): value is T => {
  return typeof value === typeof defaultValue
}

/**
 * Retrieves a cached value from the local storage based on a composite key.
 * If the value is not found, a default value is returned.
 *
 * @template T - The type of the value to be retrieved.
 * @param pixel - A string used as part of the composite key.
 * @param key - A string used as part of the composite key.
 * @param valueTypes - The default value to return if the key is not found in local storage.
 * @returns The cached value parsed as type `T`, or the provided default value if the key is not found.
 */
export const getCacheStoreFromLocal = <T>(pixel: string, key: string, valueTypes: T): T => {
  const cache = localStorage.getItem((pixel + '_' + key).toLocaleUpperCase())
  if (cache) {
    const parsed = JSON.parse(cache)
    return isValidType(parsed, valueTypes) ? parsed : valueTypes
  }
  return valueTypes
}
